/**
 * Adventure Discussions Component
 * Lists all discussions for an adventure with the ability to create new discussions
 * Mirrors the GroupDiscussions component pattern
 */

import { useState, useMemo, useRef, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemButton,
  CircularProgress,
  Alert,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  Divider,
  Select,
  FormControl,
  InputLabel,
  Avatar,
} from '@mui/material'
import {
  Add as AddIcon,
  Forum as ForumIcon,
  MoreVert as MoreVertIcon,
  Flag as FlagIcon,
  ChatBubbleOutline as CommentIcon,
  Sort as SortIcon,
  Close as CloseIcon,
  Link as LinkIcon,
  CameraAlt as CameraIcon,
  Person as PersonIcon,
  AccessTime as TimeIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material'
import { socialApi } from '@api/endpoints/social'
import { DiscussionDto, CreateDiscussionRequest, FlagPostRequest } from '@api/types/social'
import { formatDistanceToNow } from 'date-fns'
import { DiscussionThread } from '@features/social/components/DiscussionThread'
import { ReportContentDialog } from '@features/tickets/components'
import { CreateTicketRequest } from '@api/types/tickets'

interface AdventureDiscussionsProps {
  adventureId: string
  adventureTitle: string
  canPost: boolean
  canReply?: boolean
  isModerator?: boolean
  initialDiscussionId?: string
  highlightedPostId?: string
}

type SortOption = 'newest' | 'oldest' | 'mostActive' | 'mostReplies' | 'recentlyUpdated'

function safeFormatDistanceToNow(dateString: string | undefined | null): string {
  if (!dateString) return 'recently'
  try {
    const hasTimezone = dateString.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateString)
    const utcString = hasTimezone ? dateString : dateString + 'Z'
    const date = new Date(utcString)
    if (isNaN(date.getTime())) return 'recently'
    return formatDistanceToNow(date, { addSuffix: true })
  } catch {
    return 'recently'
  }
}

export function AdventureDiscussions({
  adventureId,
  adventureTitle,
  canPost,
  canReply = true,
  isModerator = false,
  initialDiscussionId,
  highlightedPostId,
}: AdventureDiscussionsProps) {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [newDiscussionTitle, setNewDiscussionTitle] = useState('')
  const [newDiscussionContent, setNewDiscussionContent] = useState('')
  const [selectedDiscussionId, setSelectedDiscussionId] = useState<string | null>(initialDiscussionId || null)
  const [menuAnchor, setMenuAnchor] = useState<{ element: HTMLElement; discussionId: string } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<SortOption>('recentlyUpdated')
  const [postSortBy, setPostSortBy] = useState<'oldest' | 'newest' | 'activity'>('oldest')
  const [sortMenuAnchor, setSortMenuAnchor] = useState<HTMLElement | null>(null)
  const [selectedImage, setSelectedImage] = useState<File | null>(null)
  const [imagePreview, setImagePreview] = useState<string | null>(null)
  const [uploadingImage, setUploadingImage] = useState(false)
  const [isDragOver, setIsDragOver] = useState(false)
  const [reportDialogOpen, setReportDialogOpen] = useState(false)
  const [reportingDiscussion, setReportingDiscussion] = useState<DiscussionDto | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    if (initialDiscussionId) {
      setSelectedDiscussionId(initialDiscussionId)
    }
  }, [initialDiscussionId])

  // Fetch discussions for this adventure
  const {
    data: discussions,
    isLoading,
    error: fetchError,
  } = useQuery({
    queryKey: ['discussions', 'adventure', adventureId],
    queryFn: () => socialApi.getDiscussions(undefined, undefined, adventureId),
    refetchOnWindowFocus: false,
  })

  // Sort discussions
  const sortedDiscussions = useMemo(() => {
    if (!discussions) return []
    const sorted = [...discussions]
    switch (sortBy) {
      case 'newest':
        return sorted.sort((a, b) => new Date(b.createdDate).getTime() - new Date(a.createdDate).getTime())
      case 'oldest':
        return sorted.sort((a, b) => new Date(a.createdDate).getTime() - new Date(b.createdDate).getTime())
      case 'mostActive':
        return sorted.sort((a, b) => {
          const aScore = new Date(a.lastUpdated).getTime() + (a.postCount * 1000 * 60 * 60)
          const bScore = new Date(b.lastUpdated).getTime() + (b.postCount * 1000 * 60 * 60)
          return bScore - aScore
        })
      case 'mostReplies':
        return sorted.sort((a, b) => b.postCount - a.postCount)
      case 'recentlyUpdated':
        return sorted.sort((a, b) => new Date(b.lastUpdated).getTime() - new Date(a.lastUpdated).getTime())
      default:
        return sorted
    }
  }, [discussions, sortBy])

  // Create discussion mutation
  const createDiscussionMutation = useMutation({
    mutationFn: (request: CreateDiscussionRequest) => socialApi.createDiscussion(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['discussions', 'adventure', adventureId] })
      setCreateDialogOpen(false)
      setNewDiscussionTitle('')
      setNewDiscussionContent('')
      setSelectedImage(null)
      setImagePreview(null)
      setError(null)
    },
    onError: (err) => {
      setError(err instanceof Error ? err.message : 'Failed to create discussion')
    },
  })

  const handleCreateDiscussion = async () => {
    if (!newDiscussionTitle.trim() || !newDiscussionContent.trim()) {
      setError('Both title and content are required')
      return
    }

    let imageId: string | undefined

    if (selectedImage) {
      setUploadingImage(true)
      try {
        const response = await socialApi.uploadDiscussionImage(selectedImage)
        imageId = response.imageId
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to upload image')
        setUploadingImage(false)
        return
      }
      setUploadingImage(false)
    }

    await createDiscussionMutation.mutateAsync({
      adventureId,
      title: newDiscussionTitle,
      content: newDiscussionContent,
      imageId,
    })

    setSelectedImage(null)
    setImagePreview(null)
  }

  const handleImageSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setSelectedImage(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handleRemoveImage = () => {
    setSelectedImage(null)
    setImagePreview(null)
    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const processImageFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file')
      return
    }

    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB')
      return
    }

    setSelectedImage(file)
    const reader = new FileReader()
    reader.onloadend = () => {
      setImagePreview(reader.result as string)
    }
    reader.readAsDataURL(file)
  }

  const handlePaste = (event: React.ClipboardEvent) => {
    const items = event.clipboardData?.items
    if (!items) return

    for (let i = 0; i < items.length; i++) {
      if (items[i].type.indexOf('image') !== -1) {
        const file = items[i].getAsFile()
        if (file) {
          event.preventDefault()
          processImageFile(file)
          break
        }
      }
    }
  }

  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(true)
  }

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)
  }

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault()
    event.stopPropagation()
    setIsDragOver(false)

    const files = event.dataTransfer?.files
    if (files && files.length > 0) {
      const file = files[0]
      if (file.type.startsWith('image/')) {
        processImageFile(file)
      }
    }
  }

  const handleCloseDialog = () => {
    setCreateDialogOpen(false)
    setNewDiscussionTitle('')
    setNewDiscussionContent('')
    setSelectedImage(null)
    setImagePreview(null)
    setError(null)
  }

  const handleCopyDiscussionLink = () => {
    if (menuAnchor) {
      const discussionId = menuAnchor.discussionId
      const url = `${window.location.origin}/adventures/${adventureId}?tab=discussion&discussionId=${discussionId}`
      navigator.clipboard.writeText(url).catch(err => {
        console.error('Failed to copy link:', err)
      })
    }
    setMenuAnchor(null)
  }

  const handleFlagDiscussion = () => {
    if (menuAnchor) {
      const discussion = sortedDiscussions?.find((d) => d.id === menuAnchor.discussionId)
      if (discussion) {
        setReportingDiscussion(discussion)
        setReportDialogOpen(true)
      }
    }
    setMenuAnchor(null)
  }

  const handleSubmitReport = async (request: CreateTicketRequest) => {
    if (reportingDiscussion) {
      const flagRequest: FlagPostRequest = {
        message: request.description,
        reportTags: [],
        reportToGroupModerators: false,
      }
      await socialApi.flagDiscussion(reportingDiscussion.id, flagRequest)
    }
    setReportDialogOpen(false)
    setReportingDiscussion(null)
  }

  const handleBackToList = () => {
    setSelectedDiscussionId(null)
    queryClient.invalidateQueries({ queryKey: ['discussions', 'adventure', adventureId] })
  }

  const getPostSortLabel = () => {
    switch (postSortBy) {
      case 'oldest':
        return 'Oldest First'
      case 'newest':
        return 'Newest First'
      case 'activity':
        return 'Most Active'
      default:
        return 'Oldest First'
    }
  }

  // If viewing a specific discussion
  if (selectedDiscussionId) {
    const discussion = sortedDiscussions?.find((d) => d.id === selectedDiscussionId)

    const handleScrollToReply = () => {
      window.scrollTo({
        top: document.documentElement.scrollHeight,
        behavior: 'smooth',
      })
      setTimeout(() => {
        const replyInput = document.querySelector('.discussion-reply-input input') as HTMLInputElement
        if (replyInput) {
          replyInput.focus()
        }
      }, 300)
    }

    return (
      <Box>
        <Paper sx={{ p: { xs: 0.5, sm: 1.5 }, mb: 1, borderRadius: { xs: 0, sm: 1 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Chip
              label="← Back to Discussions"
              onClick={handleBackToList}
              size="small"
              sx={{ cursor: 'pointer' }}
            />
            <Chip
              icon={<SortIcon />}
              label={getPostSortLabel()}
              onClick={(e) => setSortMenuAnchor(e.currentTarget)}
              size="small"
              sx={{ cursor: 'pointer' }}
              variant="outlined"
            />
          </Box>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            {discussion?.title}
          </Typography>
          {discussion && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              started by {discussion.creatorName} {safeFormatDistanceToNow(discussion.createdDate)},
              last reply {safeFormatDistanceToNow(discussion.lastUpdated)}
            </Typography>
          )}
        </Paper>

        <Paper sx={{ p: { xs: 0.5, sm: 1.5 }, mb: 1, borderRadius: { xs: 0, sm: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
            <Box sx={{ flex: 1 }}>
              <Box className="discussion-post-meta" sx={{ mb: 1 }}>
                <Avatar
                  src={discussion?.creatorAvatar}
                  alt={discussion?.creatorName}
                  className="discussion-avatar"
                  onClick={() => discussion && navigate(`/profile/${discussion.creatorId}`)}
                />
                <Box sx={{ flex: 1 }}>
                  <Typography
                    className="discussion-author"
                    onClick={() => discussion && navigate(`/profile/${discussion.creatorId}`)}
                  >
                    {discussion?.creatorName || 'Unknown User'}
                  </Typography>
                </Box>
              </Box>

              {discussion?.content && (
                <Typography variant="body1" className="discussion-content" sx={{ mb: 0 }}>
                  {discussion.content}
                </Typography>
              )}

              {discussion?.imageUrl && (
                <Box
                  component="img"
                  src={discussion.imageUrl}
                  alt="Discussion attachment"
                  sx={{
                    maxWidth: '100%',
                    maxHeight: 400,
                    borderRadius: 1,
                    mt: 1,
                    cursor: 'pointer',
                    border: '1px solid',
                    borderColor: 'divider',
                  }}
                  onClick={() => window.open(discussion.imageUrl, '_blank')}
                />
              )}

              <Box className="discussion-actions" sx={{ mt: 1 }}>
                <button
                  className="discussion-action-link"
                  onClick={handleScrollToReply}
                >
                  Reply
                </button>
                <Typography component="span" className="discussion-timestamp" sx={{ ml: 1 }}>
                  {discussion && safeFormatDistanceToNow(discussion.createdDate)}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Paper>

        <DiscussionThread
          discussionId={selectedDiscussionId}
          adventureId={adventureId}
          highlightedPostId={highlightedPostId}
          canReply={canReply}
          isModerator={isModerator}
          sortBy={postSortBy}
          onSortChange={setPostSortBy}
          onError={(err) => setError(err.message)}
        />

        {error && (
          <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        <Menu
          anchorEl={sortMenuAnchor}
          open={Boolean(sortMenuAnchor)}
          onClose={() => setSortMenuAnchor(null)}
        >
          <MenuItem
            selected={postSortBy === 'oldest'}
            onClick={() => {
              setPostSortBy('oldest')
              setSortMenuAnchor(null)
            }}
          >
            Oldest First
          </MenuItem>
          <MenuItem
            selected={postSortBy === 'newest'}
            onClick={() => {
              setPostSortBy('newest')
              setSortMenuAnchor(null)
            }}
          >
            Newest First
          </MenuItem>
          <MenuItem
            selected={postSortBy === 'activity'}
            onClick={() => {
              setPostSortBy('activity')
              setSortMenuAnchor(null)
            }}
          >
            Most Active
          </MenuItem>
        </Menu>
      </Box>
    )
  }

  // List view
  return (
    <Box>
      {/* Header - Compact: Sort + New Discussion on same line */}
      <Paper sx={{ p: { xs: 1, sm: 2 }, mb: 2, borderRadius: { xs: 0, sm: 1 } }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
            <SortIcon fontSize="small" color="action" sx={{ flexShrink: 0, display: { xs: 'none', sm: 'block' } }} />
            <FormControl size="small" sx={{ minWidth: 0, flex: 1, maxWidth: { xs: 120, sm: 170 } }}>
              <InputLabel>
                <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>Sort by</Box>
                <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>Sort</Box>
              </InputLabel>
              <Select
                value={sortBy}
                label="Sort by"
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                renderValue={(value) => {
                  const labels: Record<SortOption, { short: string; long: string }> = {
                    recentlyUpdated: { short: 'Recent', long: 'Recently Updated' },
                    newest: { short: 'Newest', long: 'Newest First' },
                    oldest: { short: 'Oldest', long: 'Oldest First' },
                    mostReplies: { short: 'Replies', long: 'Most Replies' },
                    mostActive: { short: 'Active', long: 'Most Active' },
                  }
                  const label = labels[value as SortOption]
                  return (
                    <>
                      <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>{label.long}</Box>
                      <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>{label.short}</Box>
                    </>
                  )
                }}
              >
                <MenuItem value="recentlyUpdated">Recently Updated</MenuItem>
                <MenuItem value="newest">Newest First</MenuItem>
                <MenuItem value="oldest">Oldest First</MenuItem>
                <MenuItem value="mostReplies">Most Replies</MenuItem>
                <MenuItem value="mostActive">Most Active</MenuItem>
              </Select>
            </FormControl>
          </Box>
          {canPost && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setCreateDialogOpen(true)}
              size="small"
              sx={{ flexShrink: 0, whiteSpace: 'nowrap' }}
            >
              <Box component="span" sx={{ display: { xs: 'none', sm: 'inline' } }}>New Discussion</Box>
              <Box component="span" sx={{ display: { xs: 'inline', sm: 'none' } }}>New</Box>
            </Button>
          )}
        </Box>
      </Paper>

      {(error || fetchError) && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error || (fetchError instanceof Error ? fetchError.message : 'Failed to load discussions')}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : !sortedDiscussions || sortedDiscussions.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <ForumIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 2 }} />
          <Typography variant="body1" color="text.secondary" gutterBottom>
            No discussions yet
          </Typography>
          {canPost && (
            <Typography variant="body2" color="text.secondary">
              Be the first to start a conversation about this adventure!
            </Typography>
          )}
        </Paper>
      ) : (
        <Paper>
          <List disablePadding>
            {sortedDiscussions.map((discussion, index) => (
              <Box key={discussion.id}>
                {index > 0 && <Divider />}
                <ListItem
                  disablePadding
                  secondaryAction={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {discussion.postCount > 0 && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <CommentIcon sx={{ fontSize: '1.25rem', color: 'text.secondary' }} />
                          <Typography variant="body2" color="text.secondary">
                            {discussion.postCount}
                          </Typography>
                        </Box>
                      )}
                      <IconButton
                        edge="end"
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation()
                          setMenuAnchor({ element: e.currentTarget, discussionId: discussion.id })
                        }}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Box>
                  }
                >
                  <ListItemButton onClick={() => setSelectedDiscussionId(discussion.id)} sx={{ py: 1.5 }}>
                    <Avatar
                      src={discussion.creatorAvatar}
                      sx={{
                        width: 40,
                        height: 40,
                        mr: 2,
                        cursor: 'pointer',
                      }}
                      onClick={(e) => {
                        e.stopPropagation()
                        navigate(`/profile/${discussion.creatorId}`)
                      }}
                    >
                      {discussion.creatorName?.charAt(0).toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1, minWidth: 0, pr: 2 }}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                        {discussion.title}
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: 'pointer',
                            '&:hover': { color: 'primary.main' },
                          }}
                          onClick={(e) => {
                            e.stopPropagation()
                            navigate(`/profile/${discussion.creatorId}`)
                          }}
                        >
                          <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {discussion.creatorName || 'Unknown User'}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <TimeIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                          <Typography variant="caption" color="text.secondary">
                            {safeFormatDistanceToNow(discussion.createdDate)}
                          </Typography>
                        </Box>
                        {discussion.lastUpdated && discussion.postCount > 0 && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <ReplyIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                            <Typography variant="caption" color="text.secondary">
                              {safeFormatDistanceToNow(discussion.lastUpdated)}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  </ListItemButton>
                </ListItem>
              </Box>
            ))}
          </List>
        </Paper>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={menuAnchor?.element}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
      >
        <MenuItem onClick={handleCopyDiscussionLink}>
          <ListItemIcon>
            <LinkIcon fontSize="small" />
          </ListItemIcon>
          Copy Link
        </MenuItem>
        <MenuItem onClick={handleFlagDiscussion}>
          <ListItemIcon>
            <FlagIcon fontSize="small" />
          </ListItemIcon>
          Report
        </MenuItem>
      </Menu>

      {/* Create Discussion Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Start a Discussion</DialogTitle>
        <DialogContent
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          sx={{
            border: isDragOver ? '2px dashed #1976d2' : 'none',
            borderRadius: 1,
            backgroundColor: isDragOver ? '#e3f2fd' : 'transparent',
            transition: 'all 0.2s',
          }}
        >
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          <TextField
            autoFocus
            fullWidth
            label="Title"
            value={newDiscussionTitle}
            onChange={(e) => setNewDiscussionTitle(e.target.value)}
            disabled={createDiscussionMutation.isPending || uploadingImage}
            sx={{ mb: 2, mt: 1 }}
          />
          <TextField
            fullWidth
            multiline
            minRows={4}
            label="Content"
            placeholder={isDragOver ? "Drop image here..." : "Start the conversation..."}
            value={newDiscussionContent}
            onChange={(e) => setNewDiscussionContent(e.target.value)}
            onPaste={handlePaste}
            disabled={createDiscussionMutation.isPending || uploadingImage}
            sx={{ mb: 1 }}
          />

          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp,image/gif"
            onChange={handleImageSelect}
            style={{ display: 'none' }}
          />

          <Box
            onClick={() => !selectedImage && fileInputRef.current?.click()}
            sx={{
              mb: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              cursor: selectedImage ? 'default' : 'pointer',
              opacity: selectedImage ? 0.5 : 1,
              '&:hover': {
                opacity: selectedImage ? 0.5 : 0.8,
              },
            }}
          >
            <CameraIcon sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
              Paste or drag images, or click to upload
            </Typography>
          </Box>

          {imagePreview && (
            <Box sx={{ mb: 2, position: 'relative', display: 'inline-block' }}>
              <Box
                component="img"
                src={imagePreview}
                alt="Preview"
                sx={{
                  maxWidth: '100%',
                  maxHeight: 300,
                  borderRadius: 1,
                  border: '1px solid',
                  borderColor: 'divider',
                }}
              />
              <IconButton
                size="small"
                onClick={handleRemoveImage}
                sx={{
                  position: 'absolute',
                  top: 4,
                  right: 4,
                  bgcolor: 'background.paper',
                  '&:hover': { bgcolor: 'background.paper' },
                }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreateDiscussion}
            disabled={createDiscussionMutation.isPending || uploadingImage || !newDiscussionTitle.trim() || !newDiscussionContent.trim()}
          >
            {uploadingImage ? 'Uploading...' : createDiscussionMutation.isPending ? 'Creating...' : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Report Dialog */}
      <ReportContentDialog
        open={reportDialogOpen}
        onClose={() => {
          setReportDialogOpen(false)
          setReportingDiscussion(null)
        }}
        onSubmit={handleSubmitReport}
        targetType="Discussion"
        targetName={reportingDiscussion?.title || 'this discussion'}
      />
    </Box>
  )
}
